/**
 * Decimal input primitives.
 *
 * This file exposes three layers so different call sites can compose what they
 * need without duplicating the normalization rules:
 *
 * 1. `normalizeDecimalInput` — pure utility that sanitizes a user-typed string
 *    (accepts "." and ",", strips junk, caps decimals, auto-prefixes "0.",
 *    collapses leading zeros, resets empty input to "0", and optionally caps
 *    the number of digits before the decimal separator).
 *
 * 2. `DecimalInput` — low-level controlled <Input> that renders the value and
 *    emits the already-normalized string on change. No React Hook Form wiring,
 *    no masking, no suffix. Use this when you need full control over the
 *    displayed value (e.g. local display state, masking overlays).
 *
 * 3. `DecimalFormField` — thin RHF wrapper around `DecimalInput` for simple
 *    numeric cells where the form value is the string the user sees.
 *
 * Min/max semantics:
 * - `maxIntegerDigits` is applied during normalization. When the user tries to
 *   type a digit past the limit, normalization discards the overflowing tail
 *   so the input simply stops accepting new integer digits — rather than
 *   clamping to a nasty-looking "9999…" string.
 * - `minValue` clamps on blur only. Clamping on keystroke would make it
 *   impossible to type values through intermediate sub-minimum states
 *   (e.g. typing "0" then "5" to reach "05" with a minimum of 1).
 *
 * Design rule: any new decimal-entry requirement should extend these building
 * blocks rather than re-implementing the sanitize-on-change loop.
 */
import { CSSProperties, FocusEventHandler } from 'react';

import { Control, FieldValues, Path } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

/** Default max digits after the decimal separator. */
export const DEFAULT_MAX_DECIMAL_PLACES = 8;

/**
 * Normalizes a user-typed numeric string:
 * - Replaces "," with "." so "1,5" becomes "1.5"
 * - Strips characters that are not digits or a dot
 * - Keeps only the first "." (drops any subsequent ones)
 * - Prefixes "0" when the value starts with "." (".5" -> "0.5")
 * - Strips insignificant leading zeros ("012" -> "12"); keeps "0" and "0.x"
 * - Truncates to at most `maxIntegerDigits` digits BEFORE the dot when set,
 *   so typing past the limit is silently ignored instead of clamping to a
 *   wall of nines
 * - Truncates to at most `maxDecimalPlaces` digits AFTER the dot
 * - Returns "0" for empty input by default; callers can opt into retaining an empty value
 *
 * Note: `minValue` is intentionally NOT enforced here — clamping on every
 * keystroke prevents the user from typing past intermediate sub-minimum states.
 * Apply the minimum on blur instead (see `clampToMinValue`).
 */
export const normalizeDecimalInput = (
  value: string,
  maxDecimalPlaces: number = DEFAULT_MAX_DECIMAL_PLACES,
  maxIntegerDigits?: number,
  allowNegative: boolean = false,
  allowEmpty: boolean = false
): string => {
  // Odd number of minus signs → negative (pressing "-" anywhere toggles the sign)
  const shouldBeNegative = allowNegative && (value.match(/-/g) || []).length % 2 === 1;
  const sanitized = value.replace(/,/g, '.').replace(/[^0-9.]/g, '');

  if (sanitized === '') return shouldBeNegative ? '-' : allowEmpty ? '' : '0';

  // Keep only the first dot
  const firstDotIndex = sanitized.indexOf('.');
  let singleDot =
    firstDotIndex === -1
      ? sanitized
      : sanitized.slice(0, firstDotIndex + 1) +
        sanitized.slice(firstDotIndex + 1).replace(/\./g, '');

  if (singleDot.startsWith('.')) {
    singleDot = `0${singleDot}`;
  }

  // Strip insignificant leading zeros in the integer part
  const dotIndex = singleDot.indexOf('.');
  const integerPart = dotIndex === -1 ? singleDot : singleDot.slice(0, dotIndex);
  const fractionalPart = dotIndex === -1 ? '' : singleDot.slice(dotIndex);
  const trimmedInteger = integerPart.replace(/^0+(?=\d)/, '') || '0';

  // Cap integer digits. We truncate the overflowing tail so the input simply
  // refuses extra digits rather than replacing the value with a "9999…" wall.
  // "0" is preserved because the placeholder zero isn't really a user digit.
  const cappedInteger =
    maxIntegerDigits !== undefined && trimmedInteger !== '0'
      ? trimmedInteger.slice(0, maxIntegerDigits)
      : trimmedInteger;

  const withoutLeadingZeros = `${cappedInteger}${fractionalPart}`;

  // Truncate decimal places. When maxDecimalPlaces is 0, drop the dot entirely
  // so "100." never surfaces as a display value.
  const separatorIndex = withoutLeadingZeros.indexOf('.');
  let result: string;
  if (separatorIndex === -1) {
    result = withoutLeadingZeros;
  } else if (maxDecimalPlaces <= 0) {
    result = withoutLeadingZeros.slice(0, separatorIndex);
  } else {
    result = withoutLeadingZeros.slice(0, separatorIndex + 1 + maxDecimalPlaces);
  }

  if (shouldBeNegative && result !== '0') {
    return `-${result}`;
  }
  return result;
};

/**
 * Clamps a normalized numeric string to `minValue`. Returns the input unchanged
 * if it is already >= `minValue` or if `minValue` is not provided. Intended for
 * on-blur clamping — see the module-level comment for why min is not enforced
 * on keystroke.
 */
export const clampToMinValue = (value: string, minValue?: number): string => {
  if (minValue === undefined) return value;
  const parsed = Number(value);
  if (isNaN(parsed) || parsed >= minValue) return value;
  return String(minValue);
};

export interface DecimalInputProps {
  /** Current displayed value. Callers fully own the value (controlled). */
  value: string;
  /** Called with the already-normalized string on every keystroke. */
  onValueChange: (normalized: string) => void;
  /** Max digits after the separator (default: {@link DEFAULT_MAX_DECIMAL_PLACES}). */
  maxDecimalPlaces?: number;
  /**
   * Max digits BEFORE the separator. Extra keystrokes are dropped so the input
   * stops accepting digits past the limit instead of clamping to "999…".
   */
  maxIntegerDigits?: number;
  /** Optional numeric floor. Enforced on blur only (see module comment). */
  minValue?: number;
  /** When true, a leading minus sign is preserved (for discount/credit values). */
  allowNegative?: boolean;
  /** When true, clearing the input preserves an empty value instead of resetting to zero. */
  allowEmpty?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Override the `inputMode` attribute. Defaults to "decimal". Pass `undefined`
   * when the input is rendering a non-numeric placeholder (e.g. a masked rate).
   */
  inputMode?: 'decimal' | undefined;
  className?: string;
  style?: CSSProperties;
  /** Optional ref passthrough for RHF integration. */
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

/**
 * Low-level controlled decimal input. Accepts both "." and "," and emits a
 * normalized string via `onValueChange`. Parent owns the displayed value.
 */
export const DecimalInput = ({
  value,
  onValueChange,
  maxDecimalPlaces = DEFAULT_MAX_DECIMAL_PLACES,
  maxIntegerDigits,
  minValue,
  allowNegative = false,
  allowEmpty = false,
  placeholder,
  disabled = false,
  inputMode = 'decimal',
  className,
  style,
  name,
  onBlur,
}: DecimalInputProps) => {
  return (
    <Input
      // text + inputMode so both "." and "," are accepted and we stay in
      // control of validation. Native type="number" rejects commas in many
      // locales and clears the field when a second separator is typed.
      type="text"
      inputMode={inputMode}
      name={name}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      style={style}
      value={value}
      onBlur={e => {
        // Clamp to minValue on blur so the user can type through intermediate
        // sub-minimum states without being fought by the input.
        const clamped = clampToMinValue(value, minValue);
        if (clamped !== value) {
          onValueChange(clamped);
        }
        onBlur?.(e);
      }}
      onChange={e => {
        const raw = e.target.value;
        // Non-empty input with no digits or minus signs = pure non-numeric
        // keystroke (e.g. "a" typed into an empty field) — ignore it.
        if (raw !== '' && !/[\d-]/.test(raw)) return;
        const normalized = normalizeDecimalInput(
          raw,
          maxDecimalPlaces,
          maxIntegerDigits,
          allowNegative,
          allowEmpty
        );
        onValueChange(normalized);
      }}
    />
  );
};

interface DecimalFormFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Max digits after the separator (default: {@link DEFAULT_MAX_DECIMAL_PLACES}). */
  maxDecimalPlaces?: number;
  /**
   * Max digits BEFORE the separator. Extra keystrokes are dropped so the input
   * stops accepting digits past the limit.
   */
  maxIntegerDigits?: number;
  /** Optional numeric floor. Enforced on blur only. */
  minValue?: number;
  /** When true, a leading minus sign is preserved (for discount/credit values). */
  allowNegative?: boolean;
}

/**
 * React Hook Form wrapper around `DecimalInput`.
 *
 * Stores the normalized string directly in the form. If you need a numeric
 * form value, coerce with `Number(...)` at the consumer (or use a richer
 * wrapper that handles the conversion).
 */
export const DecimalFormField = <TFieldValues extends FieldValues>({
  control,
  name,
  disabled = false,
  className,
  placeholder,
  maxDecimalPlaces = DEFAULT_MAX_DECIMAL_PLACES,
  maxIntegerDigits,
  minValue,
  allowNegative = false,
}: DecimalFormFieldProps<TFieldValues>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <DecimalInput
              name={field.name}
              value={(field.value ?? '') as string}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              placeholder={placeholder}
              className={className}
              maxDecimalPlaces={maxDecimalPlaces}
              maxIntegerDigits={maxIntegerDigits}
              minValue={minValue}
              allowNegative={allowNegative}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
